import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREDIT_WARN_RATIO, creditUsageRatio, formatCredits } from '@hub/contracts'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent } from '@/lib/collab/presence-context'
import { useDateLocale } from '@/i18n/dates'
import { cn } from '@/lib/utils'

type Usage = Awaited<ReturnType<typeof orpc.credits.getUsage>>

/**
 * Credit budget for the project, just above the Settings link (expanded
 * sidebar only): 0–100 % of the tightest limit that applies (the project cap
 * when set, else the organization's). Amber from 80 %, red once the limit is
 * reached; the tooltip carries the numbers. Refetches on the
 * `credits:changed` project event, so it moves as soon as a reply, a TTS
 * generation or a dictation is booked — no reload.
 */
export function UsageBar({ projectId }: { projectId: string }) {
  const { t } = useTranslation('common')
  const { intl } = useDateLocale()
  const [usage, setUsage] = useState<Usage | null>(null)

  const refresh = useCallback(() => {
    orpc.credits
      .getUsage({ projectId })
      .then(setUsage)
      .catch(() => setUsage(null))
  }, [projectId])

  useEffect(refresh, [refresh])
  useProjectEvent(['credits:changed'], refresh)

  if (!usage) return null

  const ratio = creditUsageRatio({
    orgUsed: usage.org.used,
    creditLimit: usage.org.limit,
    projectUsed: usage.project.used,
    projectCap: usage.project.cap,
  })
  const percent = Math.round(ratio * 100)
  const tone = ratio >= 1 ? 'red' : ratio >= CREDIT_WARN_RATIO ? 'amber' : 'normal'
  const periodEnd = usage.org.periodEnd
    ? new Date(usage.org.periodEnd).toLocaleDateString(intl, { day: 'numeric', month: 'short' })
    : null

  const bar = (
    <Progress
      value={percent}
      className={cn(
        'h-1.5 bg-foreground/20 [&_[data-slot=progress-indicator]]:bg-foreground',
        tone === 'red' && '[&_[data-slot=progress-indicator]]:bg-destructive',
        tone === 'amber' && '[&_[data-slot=progress-indicator]]:bg-amber-500'
      )}
    />
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="px-1 py-1.5 space-y-1.5"
          aria-label={t('credits.label')}
          data-testid="credits-usage-bar"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-sidebar-foreground/60">{t('credits.label')}</span>
            <span
              className={cn(
                'text-xs tabular-nums text-sidebar-foreground/60',
                tone === 'red' && 'text-destructive',
                tone === 'amber' && 'text-amber-600 dark:text-amber-500'
              )}
            >
              {t('credits.percent', { percent })}
            </span>
          </div>
          {bar}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-64 space-y-0.5">
        {/* The bar tracks the tightest limit; the tooltip shows just that one. */}
        {usage.project.cap !== null ? (
          <p>
            {t('credits.projectLine', {
              used: formatCredits(usage.project.used, intl),
              cap: formatCredits(usage.project.cap, intl),
            })}
          </p>
        ) : (
          <p>
            {t('credits.orgLine', {
              used: formatCredits(usage.org.used, intl),
              limit: formatCredits(usage.org.limit, intl),
            })}
          </p>
        )}
        {periodEnd && <p>{t('credits.periodEnd', { date: periodEnd })}</p>}
        {usage.blocked && <p>{t('credits.blocked')}</p>}
      </TooltipContent>
    </Tooltip>
  )
}
